import { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import queryString from "query-string";

import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  useTheme
} from "@mui/material";

import AccountAccessLayout from "../layouts/AccountAccessLayout";

import AccountCreationFlow from "./AccountCreationFlow";

import { analyticsPage } from "../../helpers/analytics";

const companyAccountCreationBulletPoints = [
  {
    title: `Access Your Dashboard`,
    subtitle: `Create your account in minutes and access your dashboard immediately.`
  },
  {
    title: "See Your Emissions Structure",
    subtitle:
      "Understand how Scope 1, 2, and 3 emissions are organized without setup work."
  },
  {
    title: "Get an Initial Estimate",
    subtitle:
      "We use publicly available information to generate a starting emissions estimate you can refine over time."
  }
];

const AccountCreation = () => {
  const { palette } = useTheme();
  const { accountCreationType } = useParams();

  const [errorMsg, setErrorMsg] = useState("");

  const newAccountQueries = queryString.parse(window.location.search);
  const {
    companyId,
    email: storedEmail,
    companyName,
    stripeCustomerId
  } = newAccountQueries || {};
  const isNewCompanyAdmin = !!companyId && !!storedEmail;
  const newCompanyAdminProp = isNewCompanyAdmin && newAccountQueries;

  useEffect(() => {
    // The invite-confirmation link carries a single-use token (and, for
    // company invites, the invited email) in the query string. It needs to
    // stay in the visible URL for the rest of this flow -- a mid-form
    // reload re-parses newAccountQueries from window.location.search -- so
    // only this tracking call's own context is scrubbed, not the URL
    // itself; otherwise analytics.page() would ship the token to Segment.
    analyticsPage("account-creation", {
      page: {
        url: `${window.location.origin}${window.location.pathname}`,
        search: ""
      }
    });
  }, []);

  const buildPageTitleSubtitle = () => {
    if (isNewCompanyAdmin) {
      return {
        title: `Join ${companyName} on Aclymate to help them save the world.`,
        subtitle: `With Aclymate you'll be able to help ${companyName} easily track, reduce, and offsets their carbon footprint.`,
        imagePath: "/images/platform/account-access-header.jpg",
        bulletPoints: companyAccountCreationBulletPoints
      };
    }

    return {
      title: `${
        companyName ? `Welcome ${companyName}, y` : "Y"
      }ou're almost in`,
      subtitle:
        "Choose the fastest way to sign in or use and eMail and password as your login.",
      imagePath: "/images/account-creation-header.png",
      bulletPoints: companyAccountCreationBulletPoints
    };
  };

  // "company" is the only account-creation type this route still serves --
  // MyAclymate's own personal-signup type (and any other stale value) must
  // redirect here instead of falling through to the render below, which
  // would otherwise silently create a v3-companies account for whoever
  // still has an old /account-creation/personal link bookmarked.
  if (accountCreationType !== "company") {
    return <Redirect to="/account-creation/company" />;
  }

  const { title, subtitle, subSubTitle, imagePath, bulletPoints } =
    buildPageTitleSubtitle();

  const accountCreationFormProps = {
    isNewCompanyAdmin: newCompanyAdminProp,
    companyId,
    stripeCustomerId
  };

  if (!isNewCompanyAdmin) {
    return (
      <Box
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
        p={4}
      >
        <Container maxWidth="sm">
          <Paper
            style={{
              overflow: "hidden"
            }}
          >
            <Box
              style={{
                backgroundColor: palette.secondary.main,
                color: "white"
              }}
            >
              <Box p={2}>
                <Typography variant="h2" color="inherit">
                  Create Your Account
                </Typography>
              </Box>
            </Box>
            <Box p={2}>
              <Grid container direction="column" spacing={4}>
                <Grid item>
                  <Typography variant="h3">{title}</Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {subtitle}
                  </Typography>
                  <Typography variant="subtitle2">{subSubTitle}</Typography>
                </Grid>
                <AccountCreationFlow
                  {...accountCreationFormProps}
                  setError={setErrorMsg}
                />
                {errorMsg && (
                  <Grid item>
                    <Typography color="error" variant="body1" align="center">
                      {errorMsg}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  const accountAccessLayoutProps = {
    formTitle: title,
    formSubtitle: subtitle,
    subSubTitle,
    imagePath,
    bulletPoints,
    accountCreationType,
    layoutType: "account-creation"
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <AccountAccessLayout {...accountAccessLayoutProps}>
        <Container maxWidth="sm">
          <Grid container direction="column" spacing={4}>
            <AccountCreationFlow {...accountCreationFormProps} />
          </Grid>
        </Container>
      </AccountAccessLayout>
    </Box>
  );
};
export default AccountCreation;
